import {
	createVettingInputSchema,
	isRichTextEmpty,
	richTextVisibleLength,
	VETTING_NOTES_MAX_LENGTH,
	type VettingDetail,
} from "@anthra/platform/domain"
import { ApiError, toDisplayable } from "@anthra/platform/errors"
import {
	describeTypes,
	formatBytes,
	type PlatformFile,
	VETTING_DRAFT_RULES,
	VETTING_NOTICE_RULES,
} from "@anthra/platform/files"
import { Breadcrumb, BreadcrumbCurrent, BreadcrumbItem } from "@anthra/ui/breadcrumb"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import { controlClasses } from "@anthra/ui/control"
import { Field } from "@anthra/ui/field"
import { TiptapEditor } from "@anthra/ui/tiptap-editor"
import { Description, PageTitle } from "@anthra/ui/typography"
import { createFileRoute, Link } from "@tanstack/react-router"
import { CheckIcon, FileTextIcon, InfoIcon, TriangleAlertIcon, UploadIcon } from "lucide-react"
import { useRef, useState } from "react"

import { useCreateVettingRequest } from "@/features/vetting/use-vetting"
import { toPlatformFile } from "@/lib/files"
import { missingUploads, useVettingDraftStore } from "@/stores/use-vetting-draft-store"

/**
 * Create vetting request (US-00-004.1/.2/.3/.20/.21).
 *
 * Built against screens/create-vetting-request.html: the breadcrumb, the
 * page head, the two-column `.layout` (form + guidance rail), the card holding
 * title/category, the two named upload slots and notes.
 *
 * TWO NAMED UPLOAD SLOTS, BOTH REQUIRED. The notice takes PDF/JPG/PNG and the
 * draft additionally takes DOCX, and a submission missing either is blocked with
 * a message against THAT slot while the title, category and notes stay exactly as
 * typed (US-00-004.2).
 *
 * The text lives in the draft store so an OTP round trip mid-form returns the
 * person here with their words intact, and the idempotency key is the
 * submission's rather than the attempt's so a retry cannot create a second
 * request (US-00-004.20/.21).
 *
 * DRAFT CLEARING: same strategy as the query form — reset on entry unless a
 * submission is in flight (`idempotencyKey` is set, which is the OTP-lapse
 * signal), and reset explicitly on Cancel and on the breadcrumb out.
 */
export const Route = createFileRoute("/_app/vetting/new")({
	beforeLoad: () => {
		const draft = useVettingDraftStore.getState()
		if (draft.idempotencyKey === null) draft.reset()
	},
	component: CreateVettingRequest,
})

function CreateVettingRequest() {
	const draft = useVettingDraftStore()
	const create = useCreateVettingRequest()
	const [errors, setErrors] = useState<Record<string, string>>({})
	// Held here rather than navigated away from — screens/create-vetting-
	// request.html's success state is a receipt on THIS route (US-00-004.1).
	const [submitted, setSubmitted] = useState<VettingDetail | null>(null)

	function submit(): void {
		const parsed = createVettingInputSchema.safeParse({
			title: draft.title,
			...(isRichTextEmpty(draft.notes) ? {} : { notes: draft.notes }),
		})

		const fieldErrors: Record<string, string> = {}
		if (!parsed.success) {
			for (const issue of parsed.error.issues) {
				const field = issue.path[0]
				if (typeof field === "string") fieldErrors[field] ??= issue.message
			}
		}
		for (const slot of missingUploads(draft)) {
			fieldErrors[slot] =
				slot === "notice"
					? "Upload the notice you received."
					: "Upload the draft response you have prepared."
		}

		if (!parsed.success || Object.keys(fieldErrors).length > 0) {
			setErrors(fieldErrors)
			return
		}
		if (!draft.notice || !draft.draft) return

		setErrors({})
		create.mutate(
			{
				input: parsed.data,
				files: { notice: draft.notice, draft: draft.draft },
				idempotencyKey: draft.submissionKey(),
			},
			{
				onSuccess: detail => {
					draft.reset()
					setSubmitted(detail)
				},
			},
		)
	}

	// Per-slot server rejection (US-00-004.3): a request forged past the
	// client is still checked by content-type sniffing, and the field the
	// service names is which upload it rejected.
	const serverFileErrors: Record<string, string> = {}
	if (create.error instanceof ApiError) {
		for (const detail of create.error.details) {
			if (detail.field === "notice" || detail.field === "draft") {
				serverFileErrors[detail.field] = detail.message
			}
		}
	}

	if (submitted) {
		return (
			<SubmittedVetting
				detail={submitted}
				onCreateAnother={() => {
					setSubmitted(null)
				}}
			/>
		)
	}

	return (
		<div
			className="flex flex-col gap-6"
			data-screen="create-vetting-request"
		>
			<Breadcrumb>
				<BreadcrumbItem>
					<Link
						to="/vetting"
						className="hover:text-foreground underline-offset-2 hover:underline"
						onClick={draft.reset}
					>
						Vetting
					</Link>
				</BreadcrumbItem>
				<BreadcrumbItem last>
					<BreadcrumbCurrent>New request</BreadcrumbCurrent>
				</BreadcrumbItem>
			</Breadcrumb>

			<div className="flex flex-col gap-1">
				{/* An <h1 className="text-page-title text-foreground"> with no type role was rendering at the browser default.
				    And the lede was 143 characters of what the form already shows —
				    two upload fields and a submit button say "upload both" better
				    than a sentence does. */}
				<PageTitle>Get your document vetted</PageTitle>
				<Description className="max-w-prose">
					Send your document for vetting and receive feedback before you proceed.
				</Description>
			</div>

			{/* Submission in progress (State 2). No fabricated byte count or
			    percentage — the API does not report upload progress — so this
			    stays a status announcement rather than a determinate bar. */}
			{create.isPending ? (
				<div
					className="rounded-card border-border bg-subtle flex items-start gap-3 border p-3"
					role="status"
					aria-label="Submission in progress"
				>
					<span
						className="size-5 shrink-0"
						aria-hidden="true"
					>
						<UploadIcon
							className="size-5 shrink-0"
							aria-hidden="true"
						/>
					</span>
					<div>
						<p className="text-body text-foreground">Submitting your request</p>
						<p className="flex min-w-0 flex-col gap-1">
							Keep this screen open. We will show you the request number as soon as it
							is created.
						</p>
						{/* INDETERMINATE, and it has to look it. The fill had no width
						    class and no inline style, so this rendered as an empty
						    track for the whole upload — the one moment the screen
						    exists to reassure somebody that something is happening.
						    There is no progress figure to show (the upload is behind
						    a single multipart request), so it takes the same
						    third-width pulse the panel's sign-in already uses.

						    `role="progressbar"` because the `aria-label` was on a
						    plain <div>, where a screen reader has nothing to attach
						    it to and drops it. */}
						<div
							className="bg-subtle rounded-pill h-1 w-full overflow-hidden"
							role="progressbar"
							aria-label="Uploading your files"
						>
							<div className="bg-primary rounded-pill h-full w-1/3 animate-pulse" />
						</div>
					</div>
				</div>
			) : null}

			<div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_20rem] lg:items-start">
				<form
					className="flex flex-col gap-2 border-0 p-0"
					onSubmit={event => {
						event.preventDefault()
						submit()
					}}
				>
					<Field
						className="flex flex-col gap-1.5"
						label="Nature of Document"
						labelClassName="text-primary"
						required
						hint="Example Notice, ITR, Agreement, etc"
						error={errors.title}
					>
						{({ id, describedBy, invalid }) => (
							<input
								id={id}
								className={cn(controlClasses, "h-11")}
								name="title"
								value={draft.title}
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={event => {
									draft.setTitle(event.target.value)
								}}
							/>
						)}
					</Field>

					<UploadSlot
						id="draft"
						label="Document to be vetted"
						labelClassName="text-primary"
						hint=""
						prompt="Choose your draft"
						inputLabel={draft.draft ? "Replace the draft" : "Upload your draft"}
						rules={VETTING_DRAFT_RULES}
						file={draft.draft}
						error={errors.draft ?? draft.rejections.draft?.message}
						serverError={serverFileErrors.draft}
						onSelect={file => {
							draft.setFile("draft", file)
						}}
						onRemove={() => {
							draft.clearFile("draft")
						}}
					/>
					<UploadSlot
						id="notice"
						label="Relevant other document"
						labelClassName="text-primary"
						hint=""
						prompt="Choose the notice"
						inputLabel={draft.notice ? "Replace the notice" : "Upload the notice"}
						rules={VETTING_NOTICE_RULES}
						file={draft.notice}
						error={errors.notice ?? draft.rejections.notice?.message}
						serverError={serverFileErrors.notice}
						onSelect={file => {
							draft.setFile("notice", file)
						}}
						onRemove={() => {
							draft.clearFile("notice")
						}}
					/>

					<Field
						className="flex flex-col gap-1.5"
						label="Description"
						labelClassName="text-primary"
						hint="Explain your requirement in detail."
						count={{
							current: richTextVisibleLength(draft.notes),
							max: VETTING_NOTES_MAX_LENGTH,
						}}
						error={errors.notes}
					>
						{({ describedBy, invalid }) => (
							<TiptapEditor
								label="Additional notes"
								placeholder=""
								value={draft.notes}
								maxLength={VETTING_NOTES_MAX_LENGTH}
								aria-describedby={describedBy}
								aria-invalid={invalid}
								onChange={draft.setNotes}
								invalid={invalid}
							/>
						)}
					</Field>

					{create.isError ? (
						<div
							className={cn(
								"rounded-card flex items-start gap-3 border p-3",
								Object.keys(serverFileErrors).length > 0
									? "border-status-in-progress-border bg-status-in-progress-bg"
									: "border-status-breach-border bg-status-breach-bg",
							)}
							role="alert"
						>
							<span
								className="size-5 shrink-0"
								aria-hidden="true"
							>
								<TriangleAlertIcon
									className="size-5 shrink-0"
									aria-hidden="true"
								/>
							</span>
							<div>
								<p className="text-body text-foreground">
									{toDisplayable(create.error).title}
								</p>
								<p className="flex min-w-0 flex-col gap-1">
									{toDisplayable(create.error).message}
								</p>
								{Object.keys(serverFileErrors).length > 0 ? (
									<ul className="flex flex-col gap-1">
										{Object.entries(serverFileErrors).map(([slot, message]) => (
											<li key={slot}>{message}</li>
										))}
									</ul>
								) : null}
								<div className="mt-1 flex flex-wrap gap-2">
									<button
										type="button"
										className={cn(buttonVariants({ variant: "primary" }))}
										onClick={submit}
									>
										Try sending again
									</button>
								</div>
							</div>
						</div>
					) : null}

					{/* `lg:hidden`. The <aside> further down is the desktop copy of this
				    same list, so without it DESKTOP rendered "What happens next"
				    twice — once here and once in the rail. _app.queries.new has
				    always had the class; this screen was written from it and
				    dropped it. */}
					<details className="border-border rounded-card border p-3 lg:hidden">
						<summary>What happens next</summary>
						<div>
							<ol className="flex flex-col gap-2">
								<li className="text-description text-subtle-foreground flex items-start gap-2">
									<span
										className="bg-subtle text-primary rounded-circle text-meta flex size-5 shrink-0 items-center justify-center font-semibold"
										aria-hidden="true"
									>
										1
									</span>
									<p className="min-w-0">
										<b>Every active expert is notified</b> the moment you
										submit.
									</p>
								</li>
								<li className="text-description text-subtle-foreground flex items-start gap-2">
									<span
										className="bg-subtle text-primary rounded-circle text-meta flex size-5 shrink-0 items-center justify-center font-semibold"
										aria-hidden="true"
									>
										2
									</span>
									<p className="min-w-0">
										<b>You get a WhatsApp message</b> the moment feedback is
										posted.
									</p>
								</li>
								<li className="text-description text-subtle-foreground flex items-start gap-2">
									<span
										className="bg-subtle text-primary rounded-circle text-meta flex size-5 shrink-0 items-center justify-center font-semibold"
										aria-hidden="true"
									>
										3
									</span>
									<p className="min-w-0">
										<b>Nothing here can be edited</b> once sent.
									</p>
								</li>
							</ol>
						</div>
					</details>
					<div className="flex flex-wrap items-center gap-2">
						<button
							type="submit"
							className={cn(buttonVariants({ variant: "primary" }))}
							disabled={create.isPending}
						>
							{create.isPending ? "Submitting…" : "Submit for vetting"}
						</button>
						<Link
							className={cn(buttonVariants({ variant: "secondary" }))}
							to="/vetting"
							onClick={draft.reset}
						>
							Cancel
						</Link>
					</div>

					{/* A visual-only duplicate of the `.actions` submit button, docked to
					    the bottom of the viewport below the desktop breakpoint (this
					    screen's own `.docked`, hidden by its CSS at ≥1024px) so the
					    control stays reachable without scrolling past "What happens
					    next" on a handset. `.actions`' button is the one accessible
					    submit control — this one is excluded from the accessibility
					    tree and from role queries so it is never announced, tabbed to
					    or matched twice, rather than a second control doing the exact
					    same thing as the one right above it. */}
					<div
						className="border-border bg-surface sticky bottom-0 flex flex-col gap-2 border-t py-3 lg:static lg:border-0 lg:bg-transparent lg:py-0"
						aria-hidden="true"
					>
						{/* <button
							type="button"
							tabIndex={-1}
							className={cn(buttonVariants({ variant: "primary" }))}
							disabled={create.isPending}
							onClick={submit}
						>
							{create.isPending ? "Submitting…" : "Submit for vetting"}
						</button> */}
					</div>
				</form>

				<aside
					className="hidden flex-col gap-4 lg:flex"
					aria-label="What happens next"
				>
					<div className="rounded-card border-border bg-card flex flex-col gap-2 border p-4">
						<h2 className="text-card-title text-foreground">What happens next</h2>
						<ol className="flex flex-col gap-2">
							<li className="text-description text-subtle-foreground flex items-start gap-2">
								<span
									className="bg-subtle text-primary rounded-circle text-meta flex size-5 shrink-0 items-center justify-center font-semibold"
									aria-hidden="true"
								>
									1
								</span>
								<p className="min-w-0">
									<b>Every active expert is notified</b> the moment you submit.
									Whoever picks it up first reviews both files.
								</p>
							</li>
							<li className="text-description text-subtle-foreground flex items-start gap-2">
								<span
									className="bg-subtle text-primary rounded-circle text-meta flex size-5 shrink-0 items-center justify-center font-semibold"
									aria-hidden="true"
								>
									2
								</span>
								<p className="min-w-0">
									<b>You get a WhatsApp message</b> with a link straight to the
									feedback the moment it is posted.
								</p>
							</li>
							<li className="text-description text-subtle-foreground flex items-start gap-2">
								<span
									className="bg-subtle text-primary rounded-circle text-meta flex size-5 shrink-0 items-center justify-center font-semibold"
									aria-hidden="true"
								>
									3
								</span>
								<p className="min-w-0">
									<b>Nothing here can be edited</b> once sent. Raise a new request
									if you need to change a file.
								</p>
							</li>
						</ol>
					</div>
				</aside>
			</div>
		</div>
	)
}

/**
 * The receipt — screens/create-vetting-request.html's own success state
 * (US-00-004.1). Stays on this route rather than redirecting, the same
 * choice as create-query's own receipt.
 */
function SubmittedVetting({
	detail,
	onCreateAnother,
}: {
	detail: VettingDetail
	onCreateAnother: () => void
}) {
	const submittedAt = new Date(detail.submittedAt).toLocaleString("en-IN", {
		timeZone: "Asia/Kolkata",
		day: "numeric",
		month: "long",
		year: "numeric",
		hour: "numeric",
		minute: "2-digit",
	})

	return (
		<div
			className="flex flex-col gap-6"
			data-screen="create-vetting-request"
		>
			<div className="rounded-card border-border bg-card shadow-card flex flex-col gap-6 border p-6 sm:p-8">
				<div className="flex flex-wrap items-start justify-between gap-x-4 gap-y-3">
					<div className="flex items-start gap-3">
						<span
							className="bg-status-answered-bg text-status-answered-fg rounded-circle flex size-10 shrink-0 items-center justify-center"
							aria-hidden="true"
						>
							<CheckIcon
								className="size-5 shrink-0"
								aria-hidden="true"
							/>
						</span>
						<div className="flex flex-col gap-0.5 pt-0.5">
							<h2 className="text-section-title text-heading">
								Vetting request created
							</h2>
							<p className="text-description text-subtle-foreground">
								It is with the expert panel now.
							</p>
						</div>
					</div>
					<div className="flex shrink-0 items-center gap-2">
						<span className="text-foreground text-meta font-semibold tabular-nums">
							#V{detail.sequence}
						</span>
						<span className="text-description capitalize">{detail.status}</span>
					</div>
				</div>

				<dl className="border-border flex flex-col border-t">
					<div className="border-border flex flex-wrap items-baseline justify-between gap-2 border-b py-2.5">
						<dt className="text-meta text-muted">Title</dt>
						<dd className="text-description text-foreground text-right">
							{detail.title}
						</dd>
					</div>
					<div className="flex flex-wrap items-baseline justify-between gap-2 py-2.5">
						<dt className="text-meta text-muted">Created time</dt>
						<dd className="text-description text-foreground text-right tabular-nums">
							{submittedAt} IST
						</dd>
					</div>
				</dl>

				<div className="flex flex-wrap gap-2">
					<Link
						className={cn(buttonVariants({ variant: "primary" }))}
						to="/vetting/$vettingId"
						params={{ vettingId: String(detail.sequence) }}
					>
						Open #V{detail.sequence}
					</Link>
					<Link
						className={cn(buttonVariants({ variant: "secondary" }))}
						to="/vetting"
					>
						Go to my vetting requests
					</Link>
					<button
						type="button"
						className={cn(buttonVariants({ variant: "ghost" }))}
						onClick={onCreateAnother}
					>
						Create another request
					</button>
				</div>
			</div>
		</div>
	)
}

/**
 * One named upload slot — screens/create-vetting-request.html's `.upload`
 * (empty) and `.file`/`.file--error` (chosen), not the generic FilePicker:
 * this screen names every one of those parts differently from create-query's.
 */
function UploadSlot({
	id,
	label,
	hint,
	prompt,
	inputLabel,
	rules,
	file,
	error,
	serverError,
	onSelect,
	onRemove,
	labelClassName,
}: {
	id: string
	label: string
	hint: string
	prompt: string
	/** The accessible name of the file input itself — "Upload the notice" and
	 *  "Replace the notice" name the same control at different points in its
	 *  life, matching the create-query picker's own labelling convention. */
	inputLabel: string
	rules: { accepted: readonly string[]; maxBytes: number }
	file: PlatformFile | null
	error: string | undefined
	/** The server's own content-sniffed rejection of the file CURRENTLY
	 *  attached — a spoofed extension that passed the client but failed the
	 *  422 check (US-00-004.3). This is the one case `.file--error` renders. */
	serverError: string | undefined
	onSelect: (file: PlatformFile) => void
	onRemove: () => void
	labelClassName?: string
}) {
	const inputRef = useRef<HTMLInputElement>(null)

	return (
		<div className="flex flex-col gap-1.5">
			<span
				className={cn("text-field-label text-foreground", labelClassName)}
				id={`${id}-label`}
			>
				{label}
			</span>
			<p className="text-meta text-muted">{hint}</p>

			<input
				ref={inputRef}
				type="file"
				className="sr-only"
				accept={rules.accepted.join(",")}
				aria-label={inputLabel}
				onChange={event => {
					const [chosen] = Array.from(event.target.files ?? [])
					if (chosen) onSelect(toPlatformFile(chosen))
					event.target.value = ""
				}}
			/>

			{file ? (
				// A REJECTED REPLACEMENT LEAVES THIS FILE ALONE (the store's own
				// rule): `file` is only ever the last ACCEPTED pick for this slot.
				// `.file--error` renders only for `serverError` — a file the client
				// accepted but the server's own content-sniffing rejected on submit.
				<div
					className={cn(
						"rounded-card border-border flex items-start gap-3 border p-3",
						serverError === undefined
							? undefined
							: "border-status-breach-border bg-status-breach-bg",
					)}
				>
					<span
						className="rounded-control bg-subtle text-muted inline-flex size-10 shrink-0 items-center justify-center"
						aria-hidden="true"
					>
						<FileTextIcon
							className="size-5 shrink-0"
							aria-hidden="true"
						/>
					</span>
					<div className="flex min-w-0 flex-1 flex-col">
						<span className="text-description text-foreground truncate">
							{file.name}
						</span>
						<span className="text-meta text-muted">
							{formatBytes(file.sizeBytes)}
							{serverError === undefined ? (
								<span className="text-status-answered-fg flex size-4 shrink-0 items-center gap-1">
									<CheckIcon
										className="size-3.5 shrink-0"
										aria-hidden="true"
									/>
									Ready
								</span>
							) : null}
						</span>
					</div>
					<button
						type="button"
						className={cn(
							buttonVariants({ variant: "ghost", size: "sm" }),
							"text-destructive",
						)}
						onClick={onRemove}
					>
						Remove
					</button>
				</div>
			) : (
				<button
					type="button"
					className="rounded-card border-border hover:border-border-strong focus-visible:ring-ring flex w-full flex-col items-center gap-2 border border-dashed p-6 text-center transition-colors focus-visible:ring-2 focus-visible:outline-none"
					data-invalid={error === undefined ? undefined : "true"}
					onClick={() => inputRef.current?.click()}
				>
					<span
						className="text-muted size-7"
						aria-hidden="true"
					>
						<UploadIcon
							className="size-7 shrink-0"
							aria-hidden="true"
						/>
					</span>
					<span className="text-description text-foreground font-medium">
						<em>{prompt}</em> or drag it here
					</span>
					<span className="text-meta text-muted">
						{/* `describeTypes`, not the raw list. This printed the MIME types
					    at the reader — "application/pdf, image/jpeg, image/png,
					    application/vnd.openxmlformats-officedocument.wordprocessingml.document"
					    — where the shared FilePicker has always said "PDF, JPG, PNG or
					    DOCX". */}
						{describeTypes(rules.accepted)} · up to {formatBytes(rules.maxBytes)}
					</span>
					<span className="flex flex-wrap justify-center gap-2">
						<span className={cn(buttonVariants({ variant: "secondary", size: "sm" }))}>
							Choose file
						</span>
					</span>
				</button>
			)}

			<div className="flex flex-wrap items-center gap-2">
				<button
					type="button"
					className={cn(buttonVariants({ variant: "secondary" }))}
					onClick={() => inputRef.current?.click()}
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<path d="M4 8.5A1.5 1.5 0 0 1 5.5 7h2l1-2h7l1 2h2a1.5 1.5 0 0 1 1.5 1.5V18a1.5 1.5 0 0 1-1.5 1.5h-13A1.5 1.5 0 0 1 4 18Z" />
						<circle
							cx="12"
							cy="13"
							r="3.25"
						/>
					</svg>
					Camera
				</button>
				<button
					type="button"
					className={cn(buttonVariants({ variant: "secondary" }))}
					onClick={() => inputRef.current?.click()}
				>
					<svg
						width="20"
						height="20"
						viewBox="0 0 24 24"
						fill="none"
						stroke="currentColor"
						strokeWidth="2"
						strokeLinecap="round"
						strokeLinejoin="round"
					>
						<rect
							x="3.5"
							y="4.5"
							width="17"
							height="15"
							rx="2"
						/>
						<path d="m4 16 4.5-4.5 3 3L17 9l3 3" />
					</svg>
					Gallery
				</button>
				<button
					type="button"
					className={cn(buttonVariants({ variant: "secondary" }))}
					onClick={() => inputRef.current?.click()}
				>
					<FileTextIcon
						className="size-5 shrink-0"
						aria-hidden="true"
					/>
					Files
				</button>
			</div>

			{error === undefined && serverError === undefined ? null : (
				<p
					role="alert"
					className="text-description text-destructive flex items-center gap-2 font-medium"
				>
					<InfoIcon
						className="size-4 shrink-0"
						aria-hidden="true"
					/>
					{serverError ?? error}
				</p>
			)}
		</div>
	)
}
